import { Link, useNavigate } from 'react-router-dom';
import { APP, SUPPORT_EMAIL } from '../../config/app';
import './Support.css';

/**
 * Public support page — the Support URL Apple requires for the App Store
 * listing (https://www.cdbooking.com/support), and the page a signed-out user
 * lands on when they cannot get into the app.
 *
 * Reachable without an account: routed outside the auth guards in App.jsx, and
 * every answer here has to make sense to someone who is locked out.
 */
export default function Support() {
  const navigate = useNavigate();

  // Opened straight from the App Store listing there is no history to go back
  // to, so the link falls through to the sign-in screen rather than dead-ending.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <main className="support">
      <button type="button" className="support__back" onClick={goBack}>
        &larr; Back
      </button>

      <h1 className="support__title">Help and support</h1>
      <p className="support__lede">
        {APP.name} is the equipment and room booking system for the National College of Art
        and Design. Students and staff use it to reserve cameras, lenses, audio kit, lab
        computers and studio spaces, to see when an item is due back, and to track fines and
        bookings.
      </p>

      <section className="support__section" aria-labelledby="support-who">
        <h2 id="support-who">Who can use it</h2>
        <p>
          Current NCAD students and staff only. It is an internal college system, not a public
          service, and it is distributed as an unlisted app.
        </p>
      </section>

      <section className="support__section" aria-labelledby="support-account">
        <h2 id="support-account">Getting an account</h2>
        <p>
          You cannot sign yourself up. Accounts are issued by your department — your
          department administrator or technical officer creates one for you and the system
          emails you a link to set your password. If you have not received that email, or your
          address has changed, ask your department administrator to send it again.
        </p>
      </section>

      <section className="support__section" aria-labelledby="support-password">
        <h2 id="support-password">Resetting your password</h2>
        <p>
          Use <Link to="/forgot-password">Forgot your password?</Link> on the sign-in screen.
          Enter your college email address and the system emails you a reset link. The link
          expires, so if it has gone stale simply request another one.
        </p>
      </section>

      <section className="support__section" aria-labelledby="support-data">
        <h2 id="support-data">Your data, and deleting your account</h2>
        <p>
          Sign in and open <strong>Me &rarr; Profile</strong>. The <strong>Your data</strong>
          {' '}section there lets you:
        </p>
        <ul>
          <li>
            <strong>Download my data</strong> — a JSON copy of everything the system holds
            about you: your profile, bookings, fines, strikes, templates, waitlist entries and
            recent activity.
          </li>
          <li>
            <strong>Request account deletion</strong> — this sends a request to the booking
            administrators, who action it by hand. Your account is issued by the college and
            attached to loan records, so deletion cannot complete while you have equipment out
            or fines unpaid, and some booking and audit records are retained as the{' '}
            <Link to="/privacy">privacy notice</Link> describes. You will be contacted by
            email, and you can withdraw the request until it is actioned.
          </li>
        </ul>
      </section>

      <section className="support__section" aria-labelledby="support-contact">
        <h2 id="support-contact">Contact</h2>
        <p>
          For anything else — a booking problem, equipment already out, or a question about
          your data — contact your department administrator, or email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
        <p>
          You can also read the full <Link to="/privacy">privacy notice</Link>, which explains
          what is collected, why, and how long it is kept.
        </p>
      </section>
    </main>
  );
}
